import { z } from 'zod';
import { Id, IsoDate } from './common';
import { EntitlementKeys, FeatureKeys, InquiryStatus, LimitKeys, OverrideScope, SubscriptionStatus, SubscriptionType, } from './enums';
/* ============================================================================
   Plans, entitlements, subscriptions, inquiries (PLAN §4)
   ========================================================================== */
/** Limit values: -1 = Unlimited. */
export const LimitValue = z.number().int().min(-1);
export const EntitlementsSchema = z.object({
    limits: z.record(z.enum(LimitKeys), LimitValue),
    features: z.record(z.enum(FeatureKeys), z.boolean()),
});
export const PlanSchema = z.object({
    id: Id,
    key: z.string(),
    name: z.string(),
    sortOrder: z.number().int(),
    archived: z.boolean(),
    currentVersion: z.number().int().positive(),
    draftVersion: z.number().int().positive().nullable(),
    entitlements: EntitlementsSchema,
    tenantCount: z.number().int().nonnegative(),
    createdAt: IsoDate,
    updatedAt: IsoDate,
});
export const PlanVersionSchema = z.object({
    id: Id,
    planId: Id,
    version: z.number().int().positive(),
    publishedAt: IsoDate.nullable(),
    entitlements: EntitlementsSchema,
    tenantCount: z.number().int().nonnegative(),
    createdAt: IsoDate,
});
export const PlanCreateSchema = z.object({
    name: z.string().trim().min(2).max(40),
    key: z
        .string()
        .regex(/^[a-z0-9_]{2,30}$/)
        .optional(),
    copyFromPlanId: Id.optional(),
});
export const PlanUpdateSchema = z.object({
    name: z.string().trim().min(2).max(40).optional(),
    sortOrder: z.number().int().optional(),
    /** edits the draft version's entitlements */
    entitlements: EntitlementsSchema.partial().optional(),
});
export const PlanPublishSchema = z.object({ migrateExisting: z.boolean() });
export const PlanReorderSchema = z.object({ planIds: z.array(Id).min(1) });
export const SubscriptionSchema = z.object({
    id: Id,
    tenantId: Id,
    plan: z.object({ id: Id, key: z.string(), name: z.string(), version: z.number().int() }),
    type: z.enum(SubscriptionType),
    status: z.enum(SubscriptionStatus),
    startsAt: IsoDate,
    endsAt: IsoDate,
    graceEndsAt: IsoDate.nullable(),
    readOnlyEndsAt: IsoDate.nullable(),
    daysLeft: z.number().int(),
    invoiceNumber: z.string().nullable(),
    amountInr: z.number().nullable(),
    notes: z.string().nullable(),
    createdAt: IsoDate,
});
export const SubscriptionAssignSchema = z.object({
    planId: Id,
    type: z.enum(SubscriptionType),
    startsAt: IsoDate.optional(),
    /** either durationMonths or endsAt */
    durationMonths: z.number().int().min(1).max(120).optional(),
    endsAt: IsoDate.optional(),
    invoiceNumber: z.string().max(60).optional(),
    amountInr: z.number().nonnegative().optional(),
    notes: z.string().max(1000).optional(),
});
export const SubscriptionExtendSchema = z.union([
    z.object({ months: z.number().int().min(1).max(60) }),
    z.object({ endsAt: IsoDate }),
]);
/** Effective entitlement row, with where its value comes from (Mission HQ + Settings → Plan). */
export const EffectiveEntitlementSchema = z.object({
    key: z.enum(EntitlementKeys),
    kind: z.enum(['limit', 'feature']),
    value: z.union([LimitValue, z.boolean()]),
    source: z.enum(['plan', 'tenant_override', 'workspace_override']),
    override: z
        .object({ id: Id, reason: z.string(), expiresAt: IsoDate.nullable(), setBy: z.string(), createdAt: IsoDate })
        .nullable(),
    usage: z.number().int().nonnegative().nullable(),
});
export const EntitlementScopeQuery = z.object({
    scope: z.enum(OverrideScope).default('tenant'),
    workspaceId: Id.optional(),
});
export const EntitlementOverrideSetSchema = z.object({
    scope: z.enum(OverrideScope),
    workspaceId: Id.optional(),
    key: z.enum(EntitlementKeys),
    value: z.union([LimitValue, z.boolean()]),
    reason: z.string().trim().min(3).max(300),
    expiresAt: IsoDate.optional(),
});
export const PlanInquirySchema = z.object({
    id: Id,
    tenant: z.object({ id: Id, name: z.string(), slug: z.string() }),
    requestedPlan: z.object({ id: Id, name: z.string() }),
    durationMonths: z.number().int().positive(),
    seats: z.number().int().positive().nullable(),
    message: z.string().nullable(),
    status: z.enum(InquiryStatus),
    requestedBy: z.object({ name: z.string(), email: z.string() }),
    createdAt: IsoDate,
    updatedAt: IsoDate,
});
export const PlanInquiryCreateSchema = z.object({
    planId: Id,
    durationMonths: z.number().int().min(1).max(120),
    seats: z.number().int().min(1).max(10000).optional(),
    message: z.string().max(1000).optional(),
});
export const PlanInquiryUpdateSchema = z.object({ status: z.enum(InquiryStatus) });
/** Tenant-side Plan & billing page. */
export const BillingSummarySchema = z.object({
    subscription: SubscriptionSchema,
    entitlements: z.array(EffectiveEntitlementSchema),
    availablePlans: z.array(z.object({ id: Id, name: z.string(), key: z.string() })),
    openInquiry: PlanInquirySchema.nullable(),
});
//# sourceMappingURL=plans.js.map